import { useCallback, useEffect, useState } from 'react';

/**
 * Diagnostic-mode toggle with load-scoped lifetime.
 *
 * - State lives in a module-level variable; any page load starts at `false`.
 * - Sidebar navigation within the same load does NOT remount this module, so
 *   the mode persists across V2 sections for the current session.
 * - Hard refresh / new browser tab / closed-and-reopened tab all reload the
 *   module and reset to `false`.
 * - Cross-component reads stay in sync via a custom "pwd-diag-changed"
 *   window event (sessionStorage's native event does not fire in the writing
 *   tab, and we deliberately avoid storage at all to keep reset-on-reload
 *   semantics).
 */

let moduleDiagEnabled = false;
const EVENT_NAME = 'pwd-diag-changed';

function broadcast(enabled: boolean): void {
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { enabled } }));
}

export function useDiagMode(): [boolean, (next: boolean) => void, () => void] {
  const [enabled, setEnabled] = useState<boolean>(moduleDiagEnabled);

  useEffect(() => {
    function handle(event: Event) {
      const custom = event as CustomEvent<{ enabled: boolean } | undefined>;
      const next = custom.detail && typeof custom.detail.enabled === 'boolean'
        ? custom.detail.enabled
        : moduleDiagEnabled;
      setEnabled(next);
    }
    window.addEventListener(EVENT_NAME, handle);
    return () => window.removeEventListener(EVENT_NAME, handle);
  }, []);

  const setMode = useCallback((next: boolean) => {
    moduleDiagEnabled = next;
    setEnabled(next);
    broadcast(next);
  }, []);

  const toggle = useCallback(() => {
    const next = !moduleDiagEnabled;
    moduleDiagEnabled = next;
    setEnabled(next);
    broadcast(next);
  }, []);

  return [enabled, setMode, toggle];
}
